import type { CurrencyCode } from "@beautypie/core";

import { isBrowser } from "@shared/utils.environment";

import type { VariantType, VariantTypeV2 } from "@shared/graphql.types";
import { productImpressions } from ".";

export type ImpressionType = {
  variant: VariantType;
  position: number;
  path: string;
};

export type ImpressionTypeV2 = {
  variant: VariantTypeV2;
  position: number;
  path: string;
};

let impressions: ImpressionType[] = [];

const impressionsV2: ImpressionTypeV2[] = [];

// array to keep impressions per page to avoid sending multiple impressions when scrolling
let pageImpressions: ImpressionType[] = [];

const pageImpressionsV2: ImpressionTypeV2[] = [];

const maxBatch = 10;
const timerDelay = 10 * 1000;
let timer = 0;

const sendImpressions = (currencyCode: CurrencyCode) => {
  if (typeof window !== "undefined" && timer) {
    window.clearTimeout(timer);
    // send stored impressions on every 10 seconds
    timer = window.setTimeout(sendImpressions, timerDelay);
  }
  if (!impressions.length) return;

  // push impressions to DataLayer
  productImpressions({ items: impressions, currencyCode });
  resetImpressions();
};

export const initGTMImpressions = (currencyCode: CurrencyCode): void => {
  if (isBrowser()) {
    // send stored impressions on every 10 seconds
    timer = window.setTimeout(sendImpressions, timerDelay);

    // listener to send existing product impressions if users leave our web site
    window.addEventListener("beforeunload", () => {
      sendImpressions(currencyCode);
    });
  }
};

export const productImpression = (
  item: ImpressionType,
  currencyCode: CurrencyCode
): void => {
  const hasImpression = pageImpressions.some(
    (impression) =>
      impression.path === item.path &&
      impression.variant.sku === item.variant.sku
  );

  if (!hasImpression) {
    pageImpressions.push(item);
    impressions.push(item);
  }

  if (impressions.length >= maxBatch) {
    sendImpressions(currencyCode);
  }
};

export const productImpressionV2 = (
  item: ImpressionTypeV2,
  currencyCode: CurrencyCode
): void => {
  const hasImpression = pageImpressionsV2.some(
    (impression) =>
      impression.path === item.path &&
      impression.variant.sku === item.variant.sku
  );

  if (!hasImpression) {
    pageImpressionsV2.push(item);
    impressionsV2.push(item);
  }

  if (impressions.length >= maxBatch) {
    sendImpressions(currencyCode);
  }
};

export const resetImpressions = (): void => {
  impressions = [];
};

export const resetPageImpressions = (currencyCode: CurrencyCode): void => {
  // push existing impressions before path change
  sendImpressions(currencyCode);

  pageImpressions = [];
};
